/**
 * Pending state shown while a route's loader is in flight.
 *
 * Without this the router keeps the previous page fully rendered for the whole
 * load, so a click reads as "nothing happened" — measured at ~1s on spec pages
 * and ~20s on a throttled connection. The shape below mirrors what every data
 * template shares: breadcrumb, H1, answer block, then a table.
 */
function Bar({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-muted ${className}`} />;
}

export function RoutePending() {
  return (
    <div className="container mx-auto px-4 py-8" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading page…</span>

      {/* breadcrumb */}
      <div className="mb-6 flex gap-2">
        <Bar className="h-3 w-12" />
        <Bar className="h-3 w-16" />
        <Bar className="h-3 w-20" />
      </div>

      {/* h1 + subtitle */}
      <Bar className="h-9 w-3/4 max-w-xl" />
      <Bar className="mt-3 h-4 w-1/2 max-w-md" />

      {/* direct-answer card */}
      <div className="mt-8 rounded-lg border border-border p-5">
        <Bar className="h-4 w-40" />
        <Bar className="mt-3 h-3 w-full" />
        <Bar className="mt-2 h-3 w-11/12" />
        <Bar className="mt-2 h-3 w-2/3" />
      </div>

      {/* stat pills */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-4">
            <Bar className="h-3 w-16" />
            <Bar className="mt-2 h-6 w-20" />
          </div>
        ))}
      </div>

      {/* table */}
      <div className="mt-8 overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-muted/40 p-3">
          <Bar className="h-3 w-32" />
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-border p-3 last:border-b-0">
            <Bar className="h-3 w-12 shrink-0" />
            <Bar className="h-3 flex-1" />
            <Bar className="hidden h-3 w-24 sm:block" />
            <Bar className="h-3 w-16 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
